import {
    ChangeDetectionStrategy,
    Component,
    computed,
    EventEmitter,
    inject,
    Input,
    OnInit,
    Output,
    signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
    IContactSelected,
    IConversation,
    IVoiceCommand,
} from '@dxp/core/models';
import { VoiceApiService } from '@dxp/core/api';
import { InitialsPipe, FormatDurationPipe } from '@dxp/core/pipes';
import { HeartbeatService, VoiceService } from '@dxp/core/services';
import { intervalToDuration } from 'date-fns';
import { WORKITEMS_STORE } from '@dxp/core/stores';

@Component({
    selector: 'voice-contacts-voice-enquiry',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, InitialsPipe, FormatDurationPipe],
    templateUrl: './voice-enquiry.component.html',
    styleUrl: './voice-enquiry.component.scss',
})
export class VoiceContactVoiceEnquiryComponent implements OnInit {
    readonly #workItemStore = inject(WORKITEMS_STORE);
    readonly #voiceApiService = inject(VoiceApiService);
    readonly #voiceService = inject(VoiceService);
    readonly #heartbeatService = inject(HeartbeatService);

    @Input({ required: true }) contactSelected!: IContactSelected;
    @Input({ required: true }) conversationId!: string;
    @Input({ required: true }) workItemId!: string;
    @Output() closeContacts = new EventEmitter<boolean>();

    readonly #now = computed(() => this.#heartbeatService.lastHeartbeat());
    readonly gotoCommand = signal('GotoCaller');
    readonly holdCommand = signal('HoldBoth');
    readonly switchText = signal('Switch Call');
    readonly patched = signal(false);
    connected = false;
    readonly enquiryCallConnected = computed(() => {
        return this.#voiceService.enquiryCallConnected();
    });

    readonly conversation = computed<IConversation | undefined>(() => {
        const workItemId = this.workItemId;
        if (!workItemId) return undefined;
        const workItem =
            this.#workItemStore
                .workItems()
                .find(item => item.workItemId === workItemId) || null;
        if (!workItem) return undefined;

        const { conversations } = workItem;
        const foundConversation = conversations.find(
            c => c.conversationId === this.conversationId,
        );

        return foundConversation;
    });

    readonly panelHighlighted = computed(() => {
        return this.#voiceService.enquiryPanelHighlighted();
    });

    readonly enableButtons = computed(() => {
        return (
            this.conversation()?.enquiryCallState !== 'Connected' &&
            this.conversation()?.enquiryCallState !== 'EnquiryOnHold' &&
            this.conversation()?.enquiryCallState !== 'AllOnHold'
        );
    });

    readonly enquiryOnHold = computed(
        () =>
            this.conversation()?.enquiryCallState === 'EnquiryOnHold' ||
            this.conversation()?.enquiryCallState === 'AllOnHold',
    );

    readonly holdText = computed(() =>
        this.conversation()?.enquiryCallState === 'EnquiryOnHold' ||
        this.conversation()?.enquiryCallState === 'AllOnHold'
            ? 'On Hold'
            : 'Hold Enquiry',
    );

    readonly holdDisabled = computed(
        () =>
            this.gotoCommand() === 'GotoEnquiry' &&
            this.conversation()?.enquiryCallState !== 'AllOnHold',
    );

    readonly enquiryTime = computed(() => {
        const start = this.#voiceService.enquiryCallStartTime();
        const now = this.#now();

        if (start && now) {
            return intervalToDuration({
                start,
                end: now,
            });
        }
        return { hours: 0, minutes: 0, seconds: 0 };
    });

    ngOnInit() {
        this.callCommand(
            'Enquiry',
            this.contactSelected.type === 'agent'
                ? `Enquiry call:vcc_operators+${this.contactSelected.identifier}`
                : `Enquiry call:${this.contactSelected.identifier}`,
        );
        this.#voiceService.panelHighlightSet.set('enquiry');
    }

    enquiryState = computed(() => {
        if (
            this.conversation()?.enquiryCallState === 'Disconnected' ||
            (this.conversation()?.enquiryCallState === 'none' && this.connected)
        ) {
            this.connected = false;
            this.endEnquiry();
        }
        switch (this.conversation()?.enquiryCallState) {
            case 'Trying':
                return 'Calling...';
            case 'Connected':
                this.connected = true;
                return 'In Call';
            case 'EnquiryOnHold':
            case 'AllOnHold':
                return 'On Hold';
            default:
                return '';
        }
    });

    callCommand(command: string, destination = '') {
        switch (command) {
            case 'GotoCaller':
                this.gotoCommand.set('GotoEnquiry');
                this.#voiceService.panelHighlightSet.set('main');
                this.switchText.set('Switch Back');
                break;
            case 'GotoEnquiry':
                this.gotoCommand.set('GotoCaller');
                this.#voiceService.panelHighlightSet.set('enquiry');
                this.switchText.set('Switch Call');
                this.holdCommand.set('HoldBoth');
                this.#voiceService.panelHighlightSet.set('enquiry');
                break;
            case 'Patch':
                this.patched.set(true);
                break;
            case 'HoldBoth':
                this.holdCommand.set('GotoEnquiry');
                this.#voiceService.panelHighlightSet.set('');
                break;
        }
        const params: IVoiceCommand = {
            conversationId: this.conversationId,
            destination: destination,
            newCallCommand: command,
        };
        this.#voiceApiService.callCommand(params);
    }

    endEnquiry() {
        this.connected = false;
        this.callCommand('EndEnquiry');
        this.closeContacts.emit(true);
    }
}
